"use client"

import { useLayoutEffect, useRef, useState } from "react"
import type React from "react"
import { annotate } from "rough-notation"
import { type RoughAnnotation } from "rough-notation/lib/model"

type AnnotationAction =
  | "highlight"
  | "underline"
  | "box"
  | "circle"
  | "strike-through"
  | "crossed-off"
  | "bracket"

interface HighlighterProps {
  children: React.ReactNode
  action?: AnnotationAction
  color?: string
  darkColor?: string
  strokeWidth?: number
  animate?: boolean
  animationDuration?: number
  iterations?: number
  padding?: number
  multiline?: boolean
  isView?: boolean
}

// Walk up the DOM to find the nearest Radix ScrollArea viewport, which is
// the real scroll container. Falls back to null (= window viewport).
function findScrollRoot(element: HTMLElement): Element | null {
  let parent = element.parentElement
  while (parent) {
    if (parent.hasAttribute("data-radix-scroll-area-viewport")) return parent
    parent = parent.parentElement
  }
  return null
}

export function Highlighter({
  children,
  action = "highlight",
  color = "#ffd1dc",
  darkColor,
  strokeWidth = 1.5,
  animate = true,
  animationDuration = 600,
  iterations = 2,
  padding = 2,
  multiline = true,
  isView = false,
}: HighlighterProps) {
  const elementRef = useRef<HTMLSpanElement>(null)
  // When isView is false we show immediately; when true we wait for intersection.
  const [isInView, setIsInView] = useState(!isView)
  const [isDark, setIsDark] = useState(
    () => typeof document !== "undefined" && document.documentElement.classList.contains("dark")
  )

  useLayoutEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains("dark"))
    })
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] })
    return () => observer.disconnect()
  }, [])

  // Set up a scroll-container-aware IntersectionObserver when isView is true.
  useLayoutEffect(() => {
    if (!isView) return
    const element = elementRef.current
    if (!element) return

    // Use the Radix ScrollArea viewport as the root so that elements are only
    // considered "in view" when they scroll into the visible docs panel area —
    // not the moment they exist anywhere in the DOM.
    const root = findScrollRoot(element)

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true)
          observer.disconnect()
        }
      },
      { root, rootMargin: "-5% 0px" }
    )

    observer.observe(element)
    return () => observer.disconnect()
  }, [isView])

  // Create and manage the rough-notation annotation.
  useLayoutEffect(() => {
    const element = elementRef.current
    if (!isInView || !element) return

    let currentAnnotation: RoughAnnotation | null = null
    let scrollRAF: number | null = null

    const resolvedColor = isDark && darkColor ? darkColor : color

    const create = (duration: number) => {
      currentAnnotation?.remove()
      currentAnnotation = annotate(element, {
        type: action,
        color: resolvedColor,
        strokeWidth,
        animate,
        animationDuration: duration,
        iterations,
        padding,
        multiline,
      })
      currentAnnotation.show()
    }

    create(animationDuration)

    // rough-notation positions its SVG relative to a DOM ancestor that may sit
    // outside the ScrollArea, so re-create at duration=0 after each scroll tick
    // to recalculate coordinates for the new scroll position.
    const handleScroll = () => {
      if (scrollRAF !== null) cancelAnimationFrame(scrollRAF)
      currentAnnotation?.hide()
      scrollRAF = requestAnimationFrame(() => {
        scrollRAF = null
        create(0)
      })
    }

    const resizeObserver = new ResizeObserver(() => {
      currentAnnotation?.hide()
      currentAnnotation?.show()
    })

    resizeObserver.observe(element)
    resizeObserver.observe(document.body)
    // capture:true intercepts scroll events before they reach their target,
    // which catches non-bubbling scroll events from nested containers.
    window.addEventListener("scroll", handleScroll, { capture: true, passive: true })

    return () => {
      currentAnnotation?.remove()
      resizeObserver.disconnect()
      window.removeEventListener("scroll", handleScroll, { capture: true })
      if (scrollRAF !== null) cancelAnimationFrame(scrollRAF)
    }
  }, [isInView, isDark, action, color, darkColor, strokeWidth, animate, animationDuration, iterations, padding, multiline])

  return (
    <span ref={elementRef} className="relative inline-block bg-transparent">
      {children}
    </span>
  )
}
